import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { deleteItems, updateStateData } from "../../../Store/actions";
import { Box } from "@mui/material";
import { SpaceBetweenStack } from "../../../components/commonComponents/StyledComponents/styled";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { checkUserDetailsFromCookie, checkUserRole } from "../../../api_calls/userManagement";
import { useNavigate } from "react-router-dom";
import { createCookie, deleteCookie, setBatchLoadingStatus, setFiltersBatchLoadingStatus } from "../../../api_calls/requestConfig";
import { fetchLoopNames, fetchVisualNames, listLoopCampaigns } from "../../../api_calls/loopCampaign";
import FilterPanel from "../../../components/commonComponents/Filters/FilterPanel";
import { fetchOwnerNames, fetchRetailerRoots } from "../../../api_calls/campaignRequests";
import { fetchStoreNames } from "../../../api_calls/infrastructure";
import { collectIdsBySharedName } from "../../../api_calls/utils";

const LoopsListFilter = () => {
  const loopCampaigns = useSelector((state) => state.userSelections);
  const verifyUserRoleName = checkUserRole();
  const navigate = useNavigate();

  const [filterDataLoaded, setFilterDataLoaded] = React.useState(false);
  const [showFilterPanel, setShowFilterPanel] = React.useState(false);

  const checkUserDetails = checkUserDetailsFromCookie();
  let userDetailObj = {};
  if (checkUserDetails.success === true) {
    userDetailObj = checkUserDetails.user_detail;
  }
  // Same user types that see the Retailer listing column
  const canUseRetailerFilter = ["cyreen", "tenant"].includes(userDetailObj.user_type);

  const loadFiltersData = async () => {
    setFiltersBatchLoadingStatus(true);
    try {
      const filterRequests = [
        fetchLoopNames(),
        fetchVisualNames(),
        fetchStoreNames(),
        fetchOwnerNames("list-loop-campaigns"),
      ];
      if (canUseRetailerFilter) {
        filterRequests.push(fetchRetailerRoots());
      }
      await Promise.all(filterRequests);
      setFilterDataLoaded(true);
    } finally {
      setFiltersBatchLoadingStatus(false);
    }
  };

  const dispatchAction = useDispatch();

  const saveLoopFilters = (filters) => {
    const selectedLoopNameIds = filters.loop_name || [];

    // let (not const): the store filter block below reassigns filterValues
    let filterValues = {
      // keep the deduplicated selection for the drawer/summary display
      loop_name: selectedLoopNameIds,
      // and the full set of ids sharing each chosen name for the backend query,
      // so duplicate loop campaign names return all matching records (see listLoopCampaigns)
      loop_name_ids: collectIdsBySharedName(selectedLoopNameIds, loopCampaigns?.loop_names_info?.loop_names_list),
      visual_name: filters.visual_name || [],
      loop_company_filter: filters.owner || [],
      // Drawer key "retailer" → Redux root_retailer_filter
      root_retailer_filter: filters.retailer || [],
      status_filter: filters.status || [],
      loop_store_filter: filters.store_name || [],
      campaign_type: filters.campaign_type || null,
    };

    const noFiltersSelected =
      (!filterValues.loop_name || filterValues.loop_name.length === 0) &&
      (!filterValues.visual_name || filterValues.visual_name.length === 0) &&
      (!filterValues.loop_company_filter || filterValues.loop_company_filter.length === 0) &&
      (!filterValues.root_retailer_filter || filterValues.root_retailer_filter.length === 0) &&
      (!filterValues.status_filter || filterValues.status_filter.length === 0) &&
      (!filterValues.loop_store_filter || filterValues.loop_store_filter.length === 0) &&
      (!filterValues.campaign_type);

    if (noFiltersSelected) {
      // If nothing is selected, clear existing filters and return
      clearfilters();
      return;
    }

    // If store filters are applied, we need to convert store IDs to number_store_cyreen for the API call
    if (
      filterValues.loop_store_filter &&
      filterValues.loop_store_filter.length > 0 &&
      typeof loopCampaigns.store_list_response_for_filters !== "undefined" &&
      loopCampaigns.store_list_response_for_filters.length > 0
    ) {
      // Convert store IDs to number_store_cyreen
      const numberStoreCyreenList = filterValues.loop_store_filter.map((storeId) => {
        const storeInfo = loopCampaigns.store_list_response_for_filters.find((store) => store.id === storeId);
        return storeInfo ? storeInfo.number_store_cyreen : null;
      }).filter((v) => !!v);

      filterValues = {
        ...filterValues,
        loop_number_store_cyreen: numberStoreCyreenList
      }
    }

    // Persist to Redux for later retrieval 
    dispatchAction(
      updateStateData({
        loop_list_filters: filterValues,
        loop_list_filter_applied: true,
      })
    );
    // Persist to cookies for session persistence
    createCookie("loop_list_filters", JSON.stringify(filterValues));
    // Reload loops (reset to page 1 but keep page size)
    listLoopCampaigns(0, loopCampaigns.loops_pagination.pageSize);
  };

  const clearfilters = () => {
    // Delete the stored cookie
    deleteCookie("loop_list_filters", true);
    // Remove filters from Redux and reset flags
    dispatchAction(deleteItems(["loop_list_filters", "filtered_loops_list", "loop_list_filter_applied"]));
    // Reload unfiltered list
    listLoopCampaigns(0, loopCampaigns.loops_pagination.pageSize);
  };

  // filters config
  const loopFiltersConfig = React.useMemo(
    () => [
      {
        key: "loop_name",
        label: "Campaign Name",
        type: "multi",
        options: () =>
          typeof loopCampaigns.loop_names_info !== "undefined"
            ? loopCampaigns.loop_names_info.loop_names_list
            : [],
      },
      {
        key: "visual_name",
        label: "Visual Name",
        type: "multi",
        options: () =>
          typeof loopCampaigns.visual_names_info !== "undefined"
            ? loopCampaigns.visual_names_info.visual_names_list
            : [],
      },
      {
        key: "status",
        label: "Progress",
        disableSearch: true,
        type: "multi",
        disableSearch: true,
        selectionParam: "label",
        options: () => [
          { value: 1, label: "Active" },
          { value: 2, label: "Scheduled" },
          { value: 3, label: "Completed" },
          { value: 4, label: "Stopped" },
        ],
      },
      {
        key: "owner",
        label: "Owner",
        type: "multi",
        options: () => loopCampaigns?.companies_info,
      },
      {
        // Drawer key "retailer"; Redux key root_retailer_filter
        key: "retailer",
        label: "Retailer",
        type: "multi",
        options: () => loopCampaigns?.retailer_roots_info,
        enabled: canUseRetailerFilter,
      },
      {
        key: "store_name",
        label: "Stores",
        type: "multi",
        // selectionParam: "cyreen_id",
        showDetailedStoreSelection: true,
        // Group by retailer when the shared hierarchy rule matches. Saved values stay store ids.
        groupStoresByRetailer: true,
        options: () => loopCampaigns?.store_names_info?.store_names_list,
      },
      {
        key: "campaign_type",
        label: "Campaign Type",
        disableSearch: true,
        type: "single",
        selectionParam: "value",
        options: () => [
          { value: 'commercial', label: 'Commercial' },
          { value: 'unlimited', label: 'Unlimited' },
        ],
      },
    ],
    [loopCampaigns, filterDataLoaded]
  );

  React.useEffect(() => {
    if (typeof loopCampaigns.loop_list_filters !== "undefined") {
      // if filters have been applied before, load options data first else filter summary won't be visible at all when we reload page with applied filters
      loadFiltersData().then(() => {
        setShowFilterPanel(true);
      });
    } else {
      // if no filters have been applied yet, we can show the filter panel immediately and load options when user clicks to open it
      setShowFilterPanel(true);
    }
  }, []);

  // Map Redux state filters to FilterPanel expected keys
  const getMappedInitialFilters = () => {
    if (!loopCampaigns.loop_list_filters) {
      return {};
    }

    return {
      loop_name: loopCampaigns.loop_list_filters.loop_name || [],
      visual_name: loopCampaigns.loop_list_filters.visual_name || [],
      status: loopCampaigns.loop_list_filters.status_filter || [],
      owner: loopCampaigns.loop_list_filters.loop_company_filter || [],
      retailer: loopCampaigns.loop_list_filters.root_retailer_filter || [],
      store_name: loopCampaigns.loop_list_filters.loop_store_filter || [],
      campaign_type: loopCampaigns.loop_list_filters.campaign_type || null,
    };
  };

  return (
    <>
      {/* ----------- filter row 1 -------- */}
      <Box>
        {
          showFilterPanel && (
            <SpaceBetweenStack
              direction={"row"}
              spacing={2}
              width={"100%"}
              justifyContent={"space-between"}
              display={"flex"}
            >
              <FilterPanel
                onFilterClick={loadFiltersData}
                filtersConfig={filterDataLoaded ? loopFiltersConfig : []}
                applyFilterFunc={saveLoopFilters}
                clearFilterFunc={clearfilters}
                initiallyAppliedVals={getMappedInitialFilters()}
                draftKeyForStore="store_name"
              />
              {/* ------ overview should be hidden for brand user ----- */}
              {verifyUserRoleName.user_role !== "Brand" && (
                <ActionButton
                  onClick={(e) => {
                    // e.preventDefault();
                    navigate("/check-campaigns/loop-overview");
                  }}
                  label={"Overview"}
                />
              )}
            </SpaceBetweenStack>
          )
        }
      </Box>
    </>
  );
};

export default LoopsListFilter;